/**
 * SkillsList Component
 *
 * Catalog table of skills. Editing requires hr.manage_team.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSkills, useDeleteSkill, type Skill } from '@/hooks/useSkills';
import { useNotify } from '@/hooks/useNotify';
import { uniqueById } from '@/utils/skillOptions';
import { useAuthStore } from '@/stores/authStore';
import {
  SKILL_CATEGORY_OPTIONS,
  categoryFromLegacyType,
  categoryLabelKey,
  isSkillCategoryKey,
  type SkillCategoryKey,
} from '@/utils/skillLevels';
import { SkillFormDialog } from './SkillFormDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/common';

function displayCategory(skill: Skill): string {
  if (skill.category && isSkillCategoryKey(skill.category)) return skill.category;
  return categoryFromLegacyType(skill.type);
}

export function SkillsList() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const canManage = useAuthStore((s) => s.hasPermission('hr.manage_team'));
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<SkillCategoryKey | ''>('');
  const [showInactive, setShowInactive] = useState(false);
  const [editSkill, setEditSkill] = useState<Skill | null>(null);
  const [deleteSkill, setDeleteSkill] = useState<Skill | null>(null);

  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = useSkills({
    ...(search ? { q: search } : {}),
    ...(categoryFilter ? { category: categoryFilter } : {}),
    ...(showInactive ? {} : { isActive: true }),
    limit: 50,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const deleteMutation = useDeleteSkill();

  const skills = uniqueById(data?.pages.flatMap((page) => page.data) ?? []);

  const handleEdit = (skill: Skill) => {
    if (!canManage) return;
    setEditSkill(skill);
  };

  const handleConfirmDelete = async () => {
    if (!deleteSkill) return;

    try {
      await deleteMutation.mutateAsync(deleteSkill.id);
      notify.success(t('skills.deleted'), t('skills.deletedDesc', { name: deleteSkill.name }));
      setDeleteSkill(null);
    } catch {
      notify.error(tc('error'), t('skills.deleteFailed'));
    }
  };

  const sortCompareFn = (a: Skill, b: Skill, key: string): number => {
    switch (key) {
      case 'name':
        return a.name.localeCompare(b.name, 'fi');
      case 'category':
        return displayCategory(a).localeCompare(displayCategory(b));
      case 'order':
        return a.sortOrder - b.sortOrder;
      case 'status':
        return Number(b.isActive) - Number(a.isActive);
      default:
        return 0;
    }
  };

  const columns: DataTableColumn<Skill>[] = [
    {
      header: t('skills.name'),
      sortKey: 'name',
      renderCell: (skill) => (
        <Typography variant="body2" fontWeight={500}>
          {skill.name}
        </Typography>
      ),
    },
    {
      header: t('skills.category'),
      sortKey: 'category',
      renderCell: (skill) => (
        <Chip
          label={t(categoryLabelKey(displayCategory(skill)))}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      header: t('skills.description'),
      renderCell: (skill) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            maxWidth: 300,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {skill.description ?? '-'}
        </Typography>
      ),
    },
    {
      header: t('skills.order'),
      sortKey: 'order',
      align: 'center',
      renderCell: (skill) => skill.sortOrder,
    },
    {
      header: tc('status'),
      sortKey: 'status',
      align: 'center',
      renderCell: (skill) => (
        <Chip
          label={skill.isActive ? t('active') : t('inactive')}
          size="small"
          color={skill.isActive ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    ...(canManage
      ? [
          {
            header: '',
            align: 'right' as const,
            renderCell: (skill: Skill) => (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                <Tooltip title={tc('edit')}>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleEdit(skill); }}
                    aria-label={tc('edit')}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={tc('delete')}>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setDeleteSkill(skill); }}
                    color="error"
                    aria-label={tc('delete')}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ),
          } satisfies DataTableColumn<Skill>,
        ]
      : []),
  ];

  if (isError) {
    return (
      <Alert severity="error">
        {t('skills.loadFailed')}
      </Alert>
    );
  }

  return (
    <Box data-testid="skills-catalog">
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 3, alignItems: 'flex-end' }}
      >
        <TextField
          placeholder={t('skills.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: 250 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t('skills.category')}</InputLabel>
          <Select
            value={categoryFilter}
            label={t('skills.category')}
            onChange={(e) => setCategoryFilter(e.target.value as SkillCategoryKey | '')}
          >
            <MenuItem value="">{t('skills.allCategories')}</MenuItem>
            {SKILL_CATEGORY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => { setShowInactive(e.target.checked); }}
              size="small"
            />
          }
          label={tc('showInactive')}
        />
      </Stack>

      <DataTable<Skill>
        columns={columns}
        data={skills}
        getRowKey={(skill) => skill.id}
        {...(canManage ? { onRowClick: handleEdit } : {})}
        sortCompareFn={sortCompareFn}
        defaultSortKey="name"
        loading={isLoading}
        emptyMessage={t('skills.noSkills')}
        ariaLabel={t('skills.title')}
        renderMobileCard={(skill) => (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                {skill.name}
              </Typography>
              <Chip
                label={skill.isActive ? t('active') : t('inactive')}
                size="small"
                color={skill.isActive ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={t(categoryLabelKey(displayCategory(skill)))}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                {skill.description && (
                  <Typography variant="caption" color="text.secondary">
                    {skill.description}
                  </Typography>
                )}
              </Box>
              {canManage && (
                <Box>
                  <Tooltip title={tc('edit')}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(skill); }} aria-label={tc('edit')} sx={{ minWidth: 44, minHeight: 44 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={tc('delete')}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteSkill(skill); }} color="error" aria-label={tc('delete')} sx={{ minWidth: 44, minHeight: 44 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>
        )}
      />

      {editSkill && (
        <SkillFormDialog
          open={!!editSkill}
          skill={editSkill}
          onClose={() => setEditSkill(null)}
        />
      )}

      <ConfirmDialog
        open={!!deleteSkill}
        title={t('skills.deleteTitle')}
        message={t('skills.deleteConfirm', { name: deleteSkill?.name })}
        confirmText={tc('delete')}
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteSkill(null)}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
